import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { STATS } from "../data/site";

// Simple count-up that animates numeric portion
const useCountUp = (target, inView, duration = 1400) => {
  const [val, setVal] = useState(0);
  const numeric = parseFloat(String(target).replace(/[^\d.]/g, "")) || 0;
  const suffix = String(target).replace(/[\d.]/g, "");

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const startTime = performance.now();
    const tick = (t) => {
      const progress = Math.min((t - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setVal(Math.floor(start + (numeric - start) * eased));
      if (progress < 1) requestAnimationFrame(tick);
      else setVal(numeric);
    };
    requestAnimationFrame(tick);
  }, [inView, numeric, duration]);

  return `${val}${suffix}`;
};

const StatItem = ({ s, i }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const display = useCountUp(s.value, inView);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: i * 0.08 }}
      data-testid={`stat-card-${s.label.toLowerCase().replace(/\s+/g, '-')}`}
      className="group p-8 md:p-10 bg-white"
    >
      <div className="flex items-center justify-between">
        <div className="w-11 h-11 rounded-xl bg-navy-50 border border-navy-100 flex items-center justify-center text-navy-800 group-hover:bg-amber-500 group-hover:border-amber-500 group-hover:text-white transition-all">
          <s.icon className="w-5 h-5" />
        </div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold font-mono">
          0{i + 1}
        </div>
      </div>
      <div className="mt-8 font-display text-5xl md:text-6xl font-extrabold text-navy-900 tracking-tighter leading-none">
        {display}
      </div>
      <div className="mt-3 text-sm text-slate-600 font-medium">{s.label}</div>
    </motion.div>
  );
};

export const Stats = ({ stats: statsProp }) => {
  const stats = statsProp || STATS;
  return (
    <section data-testid="stats-section" className="relative bg-white py-20 md:py-24">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 rounded-3xl overflow-hidden border border-slate-200 shadow-[0_8px_40px_-12px_rgba(10,37,64,0.08)]">
          {stats.map((s, i) => <StatItem key={s.label} s={s} i={i} />)}
        </div>
      </div>
    </section>
  );
};

export default Stats;
